import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, CreditCard, Truck, AlertCircle, Package, ChevronRight, Shield } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useOrders } from '../hooks/useOrders';
import { useSettings } from '../hooks/useSettings';

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, selectedZone, subtotal, shippingCost, total, clearCart } = useCart();
  const { createOrder, loading: orderLoading } = useOrders();
  const { settings } = useSettings();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    zone_id: '',
    references: '',
    notes: '',
    payment_method: 'efectivo',
  });
  const [errors, setErrors] = useState({});
  const [showSummary, setShowSummary] = useState(false);

  const [mpReady, setMpReady] = useState(false);
  const [mpLoading, setMpLoading] = useState(false);
  const [mpError, setMpError] = useState(null);
  const mpBrickContainerRef = useRef(null);
  const mpBrickInstanceRef = useRef(null);

  const mpEnabled = settings?.mercadopago?.enabled ?? false;
  const mpPublicKey = settings?.mercadopago?.public_key ?? '';

  // Get payment methods from settings or use defaults
  const paymentMethods = (settings?.payment_methods?.filter(m => m.active !== false).map((method) => ({
    id: method.name.toLowerCase().replace(/\s+/g, '_'),
    label: method.name,
    icon: getPaymentIcon(method.name),
  })) || [
    { id: 'efectivo', label: 'Efectivo', icon: '💵' },
    { id: 'transferencia', label: 'Transferencia', icon: '🏦' },
  ]);

  // Add MercadoPago option if enabled
  if (mpEnabled && mpPublicKey) {
    const hasMp = paymentMethods.some(m => m.id === 'mercadopago');
    if (!hasMp) {
      paymentMethods.push({ id: 'mercadopago', label: 'Tarjeta (MercadoPago)', icon: '💳' });
    }
  }

  function getPaymentIcon(name) {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('efectivo')) return '💵';
    if (lowerName.includes('transferencia')) return '🏦';
    if (lowerName.includes('tarjeta') || lowerName.includes('débito') || lowerName.includes('crédito')) return '💳';
    if (lowerName.includes('webpay') || lowerName.includes('mercadopago')) return '💳';
    return '💰';
  }

  // Initialize MercadoPago Brick when MP is selected
  const initMercadoPagoBrick = useCallback(async () => {
    if (!mpPublicKey || !mpBrickContainerRef.current) return;

    setMpLoading(true);
    setMpError(null);

    try {
      const mp = new window.MercadoPago(mpPublicKey, { locale: 'es-CL' });
      const bricksBuilder = mp.bricks();

      await bricksBuilder.create('cardPayment', 'mp-brick-container', {
        initialization: {
          amount: total,
          payer: {
            email: formData.email || user?.email || '',
          },
        },
        customization: {
          visual: {
            style: {
              theme: 'default',
            },
          },
          paymentMethods: {
            maxInstallments: 12,
          },
        },
        callbacks: {
          onReady: () => {
            setMpReady(true);
            setMpLoading(false);
          },
          onError: (error) => {
            setMpError(error.message || 'Error al cargar el formulario de pago');
            setMpLoading(false);
          },
        },
      });
    } catch (error) {
      setMpError('Error al inicializar MercadoPago');
      setMpLoading(false);
    }
  }, [mpPublicKey, total, formData.email, user?.email]);

  // Re-initialize brick when MP is selected
  useEffect(() => {
    if (formData.payment_method === 'mercadopago') {
      if (window.MercadoPago) {
        initMercadoPagoBrick();
      } else {
        const interval = setInterval(() => {
          if (window.MercadoPago) {
            clearInterval(interval);
            initMercadoPagoBrick();
          }
        }, 200);

        const timeout = setTimeout(() => {
          clearInterval(interval);
          if (!window.MercadoPago) {
            setMpError('No se pudo cargar MercadoPago. Verifica tu conexión.');
          }
        }, 10000);

        return () => {
          clearInterval(interval);
          clearTimeout(timeout);
        };
      }
    } else {
      mpBrickInstanceRef.current = null;
      setMpReady(false);
      setMpLoading(false);
      setMpError(null);
    }

    return () => {
      mpBrickInstanceRef.current = null;
    };
  }, [formData.payment_method, initMercadoPagoBrick]);

  // Cargar datos del usuario cuando estén disponibles
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        address: user.address || prev.address,
      }));
    }
  }, [user]);

  // Cargar zona seleccionada
  useEffect(() => {
    if (selectedZone) {
      setFormData(prev => ({
        ...prev,
        zone_id: selectedZone.id,
      }));
    }
  }, [selectedZone]);

  useEffect(() => {
    if (items.length === 0) {
      navigate('/carrito');
    }
    if (!selectedZone) {
      navigate('/carrito');
    }
  }, [items, selectedZone, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'El nombre es obligatorio';
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es obligatorio';
    if (!formData.address.trim()) newErrors.address = 'La dirección es obligatoria';
    if (!formData.payment_method) newErrors.payment_method = 'Selecciona un método de pago';
    if (formData.payment_method === 'mercadopago' && (!formData.email || !formData.email.includes('@'))) {
      newErrors.email = 'El correo electrónico es obligatorio para pagar con tarjeta';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setErrors({});

    if (formData.payment_method === 'mercadopago') {
      if (!mpReady) {
        setErrors({ submit: 'El formulario de pago no está listo. Espera un momento.' });
        return;
      }

      try {
        const mp = new window.MercadoPago(mpPublicKey, { locale: 'es-CL' });
        const cardPaymentBrick = mp.bricks().getBrick('cardPayment');

        setMpLoading(true);

        const result = await cardPaymentBrick.getPaymentFormData();

        if (!result.token) {
          setMpLoading(false);
          setErrors({ submit: 'Completa los datos de la tarjeta correctamente' });
          return;
        }

        const orderData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          references: formData.references,
          notes: formData.notes,
          zone_id: selectedZone.id,
          payment_method: 'mercadopago',
          card_token: result.token,
          payment_method_id: result.payment_method_id,
          installments: result.installments || 1,
          payer_email: formData.email,
          payer_identification_type: result.payer?.identification?.type || 'DNI',
          payer_identification_number: result.payer?.identification?.number || '',
          items: items.map(item => ({
            product_id: item.product.id,
            quantity: item.quantity,
            price: item.product.current_price || (item.product.offer_price && item.product.offer_price < item.product.price ? item.product.offer_price : item.product.price),
          })),
          subtotal,
          shipping_cost: shippingCost,
          total,
        };

        const response = await createOrder(orderData);

        if (response.success) {
          clearCart();
          navigate('/pedido-confirmado', {
            state: { order: response.data?.order || response.data }
          });
        } else {
          setMpLoading(false);
          setErrors({ submit: response.message || response.error || 'Error al procesar el pago' });
        }
      } catch (err) {
        setMpLoading(false);
        setErrors({ submit: err.message || 'Error al procesar el pago con MercadoPago' });
      }
      return;
    }

    const orderData = {
      ...formData,
      zone_id: selectedZone.id,
      items: items.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        price: item.product.current_price || (item.product.offer_price && item.product.offer_price < item.product.price ? item.product.offer_price : item.product.price),
      })),
      subtotal,
      shipping_cost: shippingCost,
      total,
    };

    const result = await createOrder(orderData);

    if (result.success) {
      clearCart();
      navigate('/pedido-confirmado', {
        state: { order: result.data?.order || result.data }
      });
    } else {
      setErrors({ submit: result.error || 'Error al crear el pedido' });
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-32 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-display font-bold text-[#1B263B]">
            Finalizar Pedido
          </h1>
          <p className="text-[#7A756F]">
            Completa los datos para confirmar tu compra
          </p>
        </div>

        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-700">
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <p className="text-sm">{errors.submit}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Info */}
          <Card>
            <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4 flex items-center gap-2">
              <Package size={20} className="text-[#E85D04]" />
              Datos de contacto
            </h2>

            <div className="space-y-4">
              <Input
                label="Nombre completo"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                required
              />

              <Input
                label="Teléfono"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
                required
              />

              <Input
                label="Correo electrónico"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
              />
            </div>
          </Card>

          {/* Delivery Address */}
          <Card>
            <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-[#E85D04]" />
              Dirección de entrega
            </h2>

            <div className="space-y-4">
              <div className="p-3 bg-[#F8F7F5] rounded-xl">
                <p className="font-medium text-[#1B263B]">{selectedZone?.name}</p>
                <p className="text-sm text-[#7A756F]">
                  Costo de envío: ${selectedZone?.cost?.toLocaleString()}
                </p>
              </div>

              <Input
                label="Dirección"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Calle, número, departamento"
                error={errors.address}
                required
              />

              <Input
                label="Referencias (opcional)"
                name="references"
                value={formData.references}
                onChange={handleChange}
                placeholder="Portón azul, timbre 3, etc."
              />

              <Input
                label="Notas adicionales (opcional)"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Instrucciones especiales para la entrega"
              />
            </div>
          </Card>

          {/* Payment Method */}
          <Card>
            <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4 flex items-center gap-2">
              <CreditCard size={20} className="text-[#E85D04]" />
              Método de pago
            </h2>

            <div className="space-y-2">
              {paymentMethods.map((method) => (
                <label
                  key={method.id}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    formData.payment_method === method.id
                      ? 'border-[#E85D04] bg-[#E85D04]/5'
                      : 'border-[#EFEDEA] hover:border-[#E85D04]/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment_method"
                    value={method.id}
                    checked={formData.payment_method === method.id}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <span className="text-2xl">{method.icon}</span>
                  <span className="flex-1 font-medium text-[#1B263B]">
                    {method.label}
                  </span>
                  {formData.payment_method === method.id && (
                    <div className="w-5 h-5 rounded-full bg-[#E85D04] flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                  )}
                </label>
              ))}
            </div>

            {errors.payment_method && (
              <p className="text-red-500 text-sm mt-2">{errors.payment_method}</p>
            )}
          </Card>

          {/* MercadoPago Brick Container */}
          {formData.payment_method === 'mercadopago' && (
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Shield size={20} className="text-[#38B000]" />
                <h2 className="font-display font-bold text-lg text-[#1B263B]">
                  Datos de tarjeta
                </h2>
              </div>

              <p className="text-sm text-[#7A756F] mb-4">
                Pago seguro procesado por MercadoPago. Tus datos están encriptados.
              </p>

              {mpError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  {mpError}
                </div>
              )}

              {mpLoading && (
                <div className="flex items-center justify-center p-8">
                  <LoadingSpinner size="md" color="#E85D04" />
                  <span className="ml-3 text-sm text-[#7A756F]">Cargando formulario de pago...</span>
                </div>
              )}

              <div
                id="mp-brick-container"
                ref={mpBrickContainerRef}
                className={mpLoading ? 'hidden' : ''}
              />
            </Card>
          )}

          {/* Order Summary Toggle */}
          <button
            type="button"
            onClick={() => setShowSummary(!showSummary)}
            className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-md"
          >
            <div className="flex items-center gap-2">
              <Truck size={20} className="text-[#E85D04]" />
              <span className="font-semibold text-[#1B263B]">
                Resumen ({items.length} productos)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-display font-bold text-[#E85D04]">
                ${total.toLocaleString()}
              </span>
              <ChevronRight
                size={20}
                className={`text-[#9A9590] transition-transform ${showSummary ? 'rotate-90' : ''}`}
              />
            </div>
          </button>

          {showSummary && (
            <Card className="animate-fadeIn">
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.product.id} className="flex justify-between text-sm">
                    <span className="text-[#7A756F]">
                      {item.quantity}x {item.product.name}
                    </span>
                    <span className="font-medium text-[#1B263B]">
                      ${((item.product.current_price || (item.product.offer_price && item.product.offer_price < item.product.price ? item.product.offer_price : item.product.price)) * item.quantity).toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="border-t border-[#EFEDEA] pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#7A756F]">Subtotal</span>
                    <span className="font-medium text-[#1B263B]">${subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#7A756F]">Envío</span>
                    <span className="font-medium text-[#1B263B]">${shippingCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="font-display font-bold text-lg text-[#1B263B]">Total</span>
                    <span className="font-display font-bold text-2xl text-[#E85D04]">
                      ${total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </form>

        {/* Fixed Bottom Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#EFEDEA] p-4">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm text-[#7A756F]">Total a pagar</p>
              <p className="font-display font-bold text-2xl text-[#1B263B]">
                ${total.toLocaleString()}
              </p>
            </div>
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={orderLoading || mpLoading}
              className="flex-shrink-0"
            >
              {(orderLoading || mpLoading) ? (
                <LoadingSpinner size="sm" color="white" />
              ) : (
                'Confirmar Pedido'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
